import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { UsersFour, CalendarCheck, BellRinging, CheckCircle, TrendUp, Buildings, CalendarDot, WarningCircle, WhatsappLogo, Phone, Receipt, ArrowCircleRight, CurrencyInr, Warning } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MetricCard } from './dashboard/MetricCard';
import { FinanceCard } from './dashboard/FinanceCard';
import { logError, logWarn } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtInr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const todayMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const MODAL_CONFIG = {
  total:     { title: 'All Candidates',       listKey: 'all_candidates',              accent: '#93C5FD', icon: UsersFour },
  today:     { title: "Today's Joinings",     listKey: 'today_joining_candidates',    accent: '#C4B5FD', icon: CalendarDot },
  upcoming:  { title: 'Upcoming Joinings',    listKey: 'upcoming_joining_candidates', accent: '#FDE047', icon: CalendarCheck },
  followups: { title: 'Follow-ups Tomorrow', listKey: 'pending_followup_candidates', accent: '#F9A8D4', icon: BellRinging },
  joined:    { title: 'Successfully Joined',  listKey: 'joined_candidates',            accent: '#A3E635', icon: CheckCircle },
};

const FINANCE_STATUS = {
  cleared: { label: 'CLEARED', bg: '#A3E635' },
  carried: { label: 'CARRIED', bg: '#FDE047' },
  due:     { label: 'DUE',     bg: '#F9A8D4' },
};

const getFinanceStatus = (row) => {
  if (row.is_cleared) return FINANCE_STATUS.cleared;
  if (row.carried_forward) return FINANCE_STATUS.carried;
  return FINANCE_STATUS.due;
};

const StatusBadge = ({ row }) => {
  const s = getFinanceStatus(row);
  return (
    <span className="px-2 py-1 text-black rounded text-xs font-bold border-2 border-black" style={{ backgroundColor: s.bg }}>
      {s.label}
    </span>
  );
};

const FINANCE_MODAL_CONFIG = {
  new_charges:    { title: 'New Charges (This Month)',     accent: '#93C5FD', icon: Receipt,           filter: (r) => !r.carried_forward },
  carried:        { title: 'Carried Forward (Prior Dues)', accent: '#FDE047', icon: ArrowCircleRight,  filter: (r) => r.carried_forward },
  collected:      { title: 'Collected This Month',         accent: '#A3E635', icon: CheckCircle,        filter: (r) => (r.paid_this_month || 0) > 0 },
  outstanding:    { title: 'Outstanding (Still to Collect)', accent: '#F9A8D4', icon: CurrencyInr,      filter: (r) => (r.balance || 0) > 0.01 },
};

// Convert raw candidate phone to a wa.me-compatible digits-only number.
// Defaults to +91 (India) when no country code is present.
const buildWhatsAppLink = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return null;
  const withCC = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCC}`;
};

const buildTelLink = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : null;
};

const Dashboard = () => {
  const { canSeeFinance, hasPerm } = useAuth();
  const canOpenReports = hasPerm('reports');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [activeFinanceModal, setActiveFinanceModal] = useState(null);

  const COLORS = useMemo(() => ['#93C5FD', '#F9A8D4', '#FDE047', '#A3E635', '#C4B5FD'], []);

  const tooltipContentStyle = useMemo(() => ({
    backgroundColor: '#0F3622',
    border: '2px solid #A3E635',
    borderRadius: '6px',
    color: '#FAFAFA',
    fontWeight: 'bold'
  }), []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      logError('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFinance = useCallback(async () => {
    if (!canSeeFinance) return;
    try {
      const { data } = await axios.get(`${API}/finance/monthly`, { params: { month: todayMonthKey() } });
      setFinance(data);
    } catch (error) {
      logError('Error fetching finance:', error);
    }
  }, [canSeeFinance]);

  useEffect(() => {
    fetchStats();
    fetchFinance();
  }, [fetchStats, fetchFinance]);

  const handleMarkJoined = useCallback(async (candidate) => {
    if (candidate.joining_status === 'Joined') return;
    try {
      const payload = {
        interview_date: candidate.interview_date,
        candidate_name: candidate.candidate_name,
        candidate_number: candidate.candidate_number,
        company_name: candidate.company_name,
        date_of_joining: candidate.date_of_joining,
        recruiter_name: candidate.recruiter_name || '',
        job_category: candidate.job_category || '',
        salary: candidate.salary || 0,
        joining_status: 'Joined',
      };
      if (canSeeFinance) {
        payload.consultancy_charge = candidate.consultancy_charge != null ? candidate.consultancy_charge : 0;
      }
      await axios.put(`${API}/candidates/${candidate.id}`, payload);
      toast.success(`${candidate.candidate_name} marked as Joined`);
      fetchStats();
      fetchFinance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as Joined');
    }
  }, [canSeeFinance, fetchStats, fetchFinance]);

  const topRecruiters = useMemo(() => {
    return [...(stats?.recruiter_stats || [])]
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .slice(0, 5);
  }, [stats?.recruiter_stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl font-bold text-white">Loading...</div>
      </div>
    );
  }

  const modalCfg = activeModal ? MODAL_CONFIG[activeModal] : null;
  const modalList = modalCfg && stats ? (stats[modalCfg.listKey] || []) : [];
  const ModalIcon = modalCfg?.icon;

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
          Dashboard
        </h1>
        <p className="text-base font-medium text-white">Track your recruitment performance at a glance</p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <MetricCard
          testId="metric-total-candidates"
          onClick={() => setActiveModal('total')}
          bgColor="bg-[#93C5FD]"
          icon={UsersFour}
          badge={<TrendUp size={20} weight="bold" />}
          count={stats?.total_candidates || 0}
          label="Total Candidates"
          hint="Click to view all"
        />
        <MetricCard
          testId="metric-today-joinings"
          onClick={() => setActiveModal('today')}
          bgColor="bg-[#C4B5FD]"
          icon={CalendarDot}
          badge={<div className="bg-[#10B981] text-white px-2 py-1 rounded text-xs font-bold">TODAY</div>}
          count={stats?.today_joinings || 0}
          label="Joining Today"
        />
        <MetricCard
          testId="metric-upcoming-joinings"
          onClick={() => setActiveModal('upcoming')}
          bgColor="bg-[#FDE047]"
          icon={CalendarCheck}
          badge={<TrendUp size={20} weight="bold" />}
          count={stats?.upcoming_joinings || 0}
          label="Upcoming Joinings"
        />
        <MetricCard
          testId="metric-pending-followups"
          onClick={() => setActiveModal('followups')}
          bgColor="bg-[#F9A8D4]"
          icon={BellRinging}
          badge={<div className="bg-[#F87171] text-white px-2 py-1 rounded text-xs font-bold">Action</div>}
          count={stats?.pending_followups || 0}
          label="Follow-ups Tomorrow"
        />
        <MetricCard
          testId="metric-joined"
          onClick={() => setActiveModal('joined')}
          bgColor="bg-[#A3E635]"
          icon={CheckCircle}
          badge={<TrendUp size={20} weight="bold" />}
          count={stats?.joined_count || 0}
          label="Successfully Joined"
        />
      </div>

      {/* Finance Summary (Owner only) */}
      {canSeeFinance && finance && (
        <div className="mb-8" data-testid="dashboard-finance-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
                <CurrencyInr size={28} weight="duotone" className="text-[#A3E635]" />
                Finance — {format(parseISO(`${finance.month}-01`), 'MMMM yyyy')}
              </h2>
              <p className="text-sm text-white font-medium">Click any card for full details · <button onClick={() => navigate('/finance')} className="underline font-bold hover:text-[#A3E635]" data-testid="dashboard-open-finance-page">Open Finance page</button></p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <FinanceCard
              testid="dashboard-finance-new-charges"
              bg="#93C5FD" icon={Receipt}
              label="Revenue Billed"
              sub="This month"
              value={fmtInr(finance.new_charges)}
              onClick={() => setActiveFinanceModal('new_charges')}
            />
            <FinanceCard
              testid="dashboard-finance-carried"
              bg="#FDE047" icon={ArrowCircleRight}
              label="Carried Forward"
              sub="From prior months"
              value={fmtInr(finance.carried_forward)}
              onClick={() => setActiveFinanceModal('carried')}
            />
            <FinanceCard
              testid="dashboard-finance-collected"
              bg="#A3E635" icon={CheckCircle}
              label="Collected"
              sub="This month"
              value={fmtInr(finance.collected_this_month)}
              onClick={() => setActiveFinanceModal('collected')}
            />
            <FinanceCard
              testid="dashboard-finance-outstanding"
              bg="#F9A8D4" icon={Warning}
              label="Outstanding"
              sub="Still to collect"
              value={fmtInr(finance.all_outstanding)}
              onClick={() => setActiveFinanceModal('outstanding')}
            />
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recruiter Performance */}
        <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] p-6">
          <div className="flex items-center justify-between gap-2 mb-6">
            <div className="flex items-center gap-2">
              <UsersFour size={24} weight="duotone" className="text-[#A3E635]" />
              <h2 className="text-2xl font-bold tracking-tight text-white" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
                Recruiter Performance
              </h2>
            </div>
            {canOpenReports && (
              <button
                data-testid="dashboard-open-reports"
                onClick={() => navigate('/reports')}
                className="text-xs font-bold text-[#A3E635] hover:text-white underline"
              >
                View full report →
              </button>
            )}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats?.recruiter_stats || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" strokeWidth={1} />
              <XAxis dataKey="name" stroke="#FAFAFA" style={{fontSize: '12px', fontWeight: 'bold'}} />
              <YAxis stroke="#FAFAFA" style={{fontSize: '12px', fontWeight: 'bold'}} />
              <Tooltip contentStyle={tooltipContentStyle} />
              <Bar dataKey="count" fill="#93C5FD" stroke="#0A0A0A" strokeWidth={2} />
              <Bar dataKey="joined" fill="#A3E635" stroke="#0A0A0A" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>

          {/* Compact ranked list — click any row to drill into Reports (owner only) */}
          {topRecruiters.length > 0 && (
            <div className="mt-4 pt-4 border-t-2 border-[#143E29] space-y-2" data-testid="recruiter-ranked-list">
              {topRecruiters.map((r) => (
                  <button
                    key={r.name}
                    data-testid={`recruiter-rank-${r.name}`}
                    onClick={() => canOpenReports && navigate('/reports')}
                    disabled={!canOpenReports}
                    className={`w-full flex items-center justify-between px-3 py-2 bg-[#0B2818] border-2 border-[#143E29] rounded-md text-sm ${canOpenReports ? 'hover:border-[#A3E635] cursor-pointer' : 'cursor-default'} transition-colors`}
                  >
                    <span className="font-bold text-white">{r.name}</span>
                    <span className="flex items-center gap-3 text-xs">
                      <span className="text-white"><span className="font-bold text-[#93C5FD]">{r.count}</span> total</span>
                      <span className="text-white"><span className="font-bold text-[#A3E635]">{r.joined}</span> joined</span>
                      {canOpenReports && <span className="text-[#A3E635] font-bold">→</span>}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* Company Distribution */}
        <div className="bg-[#0F3622] border-2 border-[#F9A8D4] rounded-md shadow-[4px_4px_0px_0px_rgba(249,168,212,0.3)] p-6">
          <div className="flex items-center gap-2 mb-6">
            <Buildings size={24} weight="duotone" className="text-[#F9A8D4]" />
            <h2 className="text-2xl font-bold tracking-tight text-white" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              Company Distribution
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stats?.company_stats || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
                stroke="#0A0A0A"
                strokeWidth={2}
              >
                {(stats?.company_stats || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipContentStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-[#0F3622] border-2 border-[#FDE047] rounded-md shadow-[4px_4px_0px_0px_rgba(253,224,71,0.3)] p-6">
        <h2 className="text-2xl font-bold tracking-tight text-white mb-6" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
          Status Distribution
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {(stats?.status_distribution || []).map((item) => (
            <div
              key={item.status}
              className="border-2 border-[#0A0A0A] rounded-md p-4 text-center"
              style={{ backgroundColor: COLORS[Math.abs(item.status.charCodeAt(0)) % COLORS.length] }}
            >
              <div className="text-2xl font-black text-[#0A0A0A] mb-1">{item.count}</div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">{item.status}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Unified Candidate List Modal */}
      <Dialog open={!!activeModal} onOpenChange={(open) => !open && setActiveModal(null)}>
        <DialogContent
          data-testid="candidate-list-modal"
          className="bg-[#0F3622] border-2 sm:max-w-[750px] max-h-[80vh] overflow-y-auto"
          style={modalCfg ? { borderColor: modalCfg.accent, boxShadow: `6px 6px 0px 0px ${modalCfg.accent}` } : {}}
        >
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-white flex items-center gap-3" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              {ModalIcon && <ModalIcon size={32} weight="duotone" style={{color: modalCfg.accent}} />}
              {modalCfg?.title}
              <span className="ml-auto text-sm font-bold px-3 py-1 rounded-md text-[#0A0A0A]" style={{backgroundColor: modalCfg?.accent}}>
                {modalList.length}
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">Dialog content</DialogDescription>
          </DialogHeader>

          {modalList.length > 0 ? (
            <div className="space-y-4 mt-4">
              {modalList.map((candidate) => (
                <div
                  key={candidate.id}
                  data-testid="modal-candidate-card"
                  className="bg-[#0B2818] border-2 rounded-md p-4"
                  style={{ borderColor: modalCfg.accent }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{candidate.candidate_name}</h3>
                      <p className="text-sm text-white font-medium">
                        <span className="font-bold text-white">Company:</span> {candidate.company_name}
                      </p>
                    </div>
                    <div className="px-3 py-1 rounded-md text-xs font-bold text-[#0A0A0A]" style={{backgroundColor: modalCfg.accent}}>
                      {candidate.joining_status.toUpperCase()}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-white font-medium">Phone:</span>
                      <p className="font-bold text-white">{candidate.candidate_number}</p>
                    </div>
                    <div>
                      <span className="text-white font-medium">Recruiter:</span>
                      <p className="font-bold text-white">{candidate.recruiter_name}</p>
                    </div>
                    <div>
                      <span className="text-white font-medium">Interview Date:</span>
                      <p className="font-bold text-white">{format(parseISO(candidate.interview_date), 'dd MMM yyyy')}</p>
                    </div>
                    <div>
                      <span className="text-white font-medium">Joining Date:</span>
                      <p className="font-bold text-white">{format(parseISO(candidate.date_of_joining), 'dd MMM yyyy')}</p>
                    </div>
                  </div>

                  {(activeModal === 'today' || activeModal === 'followups') && (
                    <div className="mt-3 pt-3 border-t-2 border-[#143E29] flex items-center gap-2">
                      <WarningCircle size={16} weight="fill" className="text-[#F87171]" />
                      <p className="text-xs font-bold text-[#F87171] uppercase">
                        {activeModal === 'today' ? 'Follow up required today!' : 'Follow up tomorrow!'}
                      </p>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t-2 border-[#143E29] flex flex-wrap gap-2">
                    {buildWhatsAppLink(candidate.candidate_number) && (
                      <a
                        data-testid={`whatsapp-${candidate.id}`}
                        href={buildWhatsAppLink(candidate.candidate_number)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#25D366] text-black font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-md border-2 border-[#25D366] hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(37,211,102,0.5)] transition-all"
                      >
                        <WhatsappLogo size={16} weight="fill" />
                        WhatsApp
                      </a>
                    )}
                    {buildTelLink(candidate.candidate_number) && (
                      <a
                        data-testid={`call-${candidate.id}`}
                        href={buildTelLink(candidate.candidate_number)}
                        className="inline-flex items-center gap-2 bg-[#0F3622] text-white font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-md border-2 border-white hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.4)] transition-all"
                      >
                        <Phone size={16} weight="fill" />
                        Call
                      </a>
                    )}
                    {candidate.joining_status !== 'Joined' && (
                      <button
                        data-testid={`dashboard-mark-joined-${candidate.id}`}
                        onClick={() => handleMarkJoined(candidate)}
                        className="inline-flex items-center gap-2 bg-[#4ADE80] text-black font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-md border-2 border-[#0A0A0A] hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                      >
                        <CheckCircle size={16} weight="fill" />
                        Mark as Joined
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarCheck size={64} weight="duotone" className="mx-auto mb-4 text-black" />
              <p className="text-lg font-bold text-white">No candidates to show</p>
              <p className="text-sm text-white mt-2">Nothing matches this filter yet.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Finance Detail Modal */}
      <Dialog open={!!activeFinanceModal} onOpenChange={(open) => !open && setActiveFinanceModal(null)}>
        <DialogContent
          data-testid="dashboard-finance-modal"
          className="bg-[#0F3622] border-2 sm:max-w-[820px] max-h-[80vh] overflow-y-auto"
          style={activeFinanceModal ? { borderColor: FINANCE_MODAL_CONFIG[activeFinanceModal].accent, boxShadow: `6px 6px 0px 0px ${FINANCE_MODAL_CONFIG[activeFinanceModal].accent}` } : {}}
        >
          {activeFinanceModal && finance && (() => {
            const cfg = FINANCE_MODAL_CONFIG[activeFinanceModal];
            const Icon = cfg.icon;
            const rows = (finance.rows || []).filter(cfg.filter);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
                    <Icon size={28} weight="duotone" style={{color: cfg.accent}} />
                    {cfg.title}
                    <span className="ml-auto text-sm font-bold px-3 py-1 rounded-md text-black" style={{backgroundColor: cfg.accent}}>{rows.length}</span>
                  </DialogTitle>
            <DialogDescription className="sr-only">Dialog content</DialogDescription>
                </DialogHeader>

                {rows.length > 0 ? (
                  <div className="space-y-3 mt-4">
                    {rows.map((r) => (
                      <div
                        key={r.candidate_id}
                        data-testid="dashboard-finance-row"
                        className="bg-[#0B2818] border-2 rounded-md p-4"
                        style={{ borderColor: cfg.accent }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-bold text-white">{r.candidate_name}</h3>
                            <p className="text-sm text-white"><span className="font-bold">Company:</span> {r.company_name}</p>
                            <p className="text-xs text-white">Joined: {r.date_of_joining ? format(parseISO(r.date_of_joining), 'dd MMM yyyy') : '—'}</p>
                          </div>
                          <StatusBadge row={r} />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-white">Charge</span>
                            <p className="font-bold text-white">{fmtInr(r.consultancy_charge)}</p>
                          </div>
                          <div>
                            <span className="text-white">Paid (Total)</span>
                            <p className="font-bold text-white">{fmtInr(r.total_paid)}</p>
                          </div>
                          <div>
                            <span className="text-white">Paid This Month</span>
                            <p className={`font-bold ${r.paid_this_month > 0 ? 'text-[#A3E635]' : 'text-white'}`}>{fmtInr(r.paid_this_month)}</p>
                          </div>
                          <div>
                            <span className="text-white">Balance</span>
                            <p className={`font-bold ${r.balance > 0.01 ? 'text-[#FDE047]' : 'text-[#A3E635]'}`}>{fmtInr(r.balance)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle size={64} weight="duotone" className="mx-auto mb-4 text-[#A3E635]" />
                    <p className="text-lg font-bold text-white">Nothing to show here</p>
                    <p className="text-sm text-white mt-2">No candidates match this slice for the current month.</p>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t-2 border-[#143E29]">
                  <button
                    data-testid="dashboard-finance-modal-open-finance"
                    onClick={() => { setActiveFinanceModal(null); navigate('/finance'); }}
                    className="w-full bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] transition-all"
                  >
                    Go to Finance page →
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
